'use client';

/**
 * Investment Plan - Custom Layout
 * All components are specific to this plan only, kept in this folder.
 */

import { PlanIntroSection } from '../common/plan-intro-section';
import { FeaturesSection } from '../common/features-section';
import { WhySoftwareSection } from '../common/why-software-section';
import { HowSoftwareWorksSection } from '../common/how-software-works-section';
import { WhatIsSoftwareSection } from '../common/what-is-software-section';
import { CommissionsSection } from '../common/commissions-section';
import { AdvantagesSection } from '../common/advantages-section';
import { WhatIsPlanSection } from './WhatIsPlanSection';
import { HowItWorksSection } from './HowItWorksSection';
import { UserPlus, TrendingUp, Zap, Award, DollarSign, Crown } from 'lucide-react';

interface MLMPlan {
    slug: string;
    name: string;
    description: string;
    content: string;
    image?: string;
    features?: string[];
}

interface PlanDetailContentProps {
    plan: MLMPlan;
}

export default function PlanDetailContent({ plan }: PlanDetailContentProps) {
    return (
        <div className="min-h-screen bg-white dark:bg-gray-950">
            <PlanIntroSection
                plan={plan}
                translationNamespace="planDetails.investmentPlan.hero"
                imageSrc="/images/mlm-plans/Investment_animate.gif"
                descriptionKeys={['description1', 'description2']}
            />
            <WhatIsPlanSection plan={plan} />
            <HowItWorksSection plan={plan} />
            <WhatIsSoftwareSection
                plan={plan}
                translationNamespace="planDetails.investmentPlan.whatIsSoftware"
                imageSrc="/images/mlm-plans/mlm-software-demo.webp"
            />
            <HowSoftwareWorksSection plan={plan} translationNamespace="planDetails.investmentPlan.howSoftwareWorks" />
            <FeaturesSection plan={plan} translationNamespace="planDetails.investmentPlan.features" />
            <CommissionsSection
                plan={plan}
                translationNamespace="planDetails.investmentPlan.commissions"
                commissions={[
                    { icon: UserPlus, color: 'primary', titleKey: 'sponsor.title', descriptionKey: 'sponsor.description' },
                    { icon: DollarSign, color: 'blue', titleKey: 'roi.title', descriptionKey: 'roi.description' },
                    { icon: Zap, color: 'green', titleKey: 'roiBooster.title', descriptionKey: 'roiBooster.description' },
                    { icon: TrendingUp, color: 'purple', titleKey: 'levelMatching.title', descriptionKey: 'levelMatching.description' },
                    { icon: Award, color: 'orange', titleKey: 'rankAchievement.title', descriptionKey: 'rankAchievement.description' },
                    { icon: Crown, color: 'pink', titleKey: 'royalty.title', descriptionKey: 'royalty.description' },
                ]}
                gridCols="3"
            />
            <AdvantagesSection
                plan={plan}
                translationNamespace="planDetails.investmentPlan.advantages"
                advantageKeys={['advantage1', 'advantage2', 'advantage3', 'advantage4', 'advantage5', 'advantage6']}
                gridCols="3"
            />
            <WhySoftwareSection plan={plan} translationNamespace="planDetails.investmentPlan.whySoftware" />
        </div>
    );
}

